import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowDown, ArrowRight, ArrowUpRight, Mail } from "lucide-react";
import { LiquidCanvas } from "../components/LiquidCanvas";
import { Marquee, Reveal, SectionLabel, SplitLines } from "../components/Motion";
import { Star } from "../components/brand/Logo";
import { WorkCard } from "../components/WorkCard";
import { CtaBand } from "../components/CtaBand";
import { useLang } from "../i18n/LangContext";
import { EMAIL, EMAIL_HREF } from "../i18n/strings";

const Hero = () => {
  const { t, path } = useLang();
  return (
    <section className="relative flex min-h-[100svh] items-end overflow-hidden pb-14 pt-28" data-testid="home-hero">
      <div className="absolute inset-0"><LiquidCanvas /></div>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/40" />
      <div className="relative mx-auto w-full max-w-[1440px] px-5 md:px-10">
        <motion.p className="label text-lav" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }}>
          {t.hero.eyebrow}
        </motion.p>
        <h1 className="mt-6 font-display text-[17vw] font-extrabold leading-[0.84] text-ivory accent-safe sm:text-[13vw] lg:text-[10.5vw] xl:text-[164px]" data-testid="hero-headline">
          <SplitLines immediate delay={1.05} lines={t.hero.lines}
            lineClass={(i) => (i === 1 ? "bg-gradient-to-r from-ivory via-lav to-lav bg-clip-text text-transparent" : i === 2 ? "flex items-center gap-[0.15em]" : "")} />
        </h1>
        <div className="mt-10 flex flex-col justify-between gap-10 md:flex-row md:items-end">
          <motion.p className="max-w-md text-base leading-relaxed text-ivory/75 md:text-lg" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5, duration: 0.8 }} data-testid="hero-subcopy">
            {t.hero.sub}
          </motion.p>
          <motion.div className="flex flex-wrap items-center gap-4" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.65, duration: 0.8 }}>
            <Link to={path("contact")} data-testid="hero-start-project-button"
              className="label group flex items-center gap-3 rounded-full bg-lav px-7 py-4 text-ink transition-colors duration-300 hover:bg-ivory">
              {t.common.startProject}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to={path("work")} data-testid="hero-view-work-button"
              className="label group flex items-center gap-3 rounded-full border border-ivory/30 px-7 py-4 text-ivory transition-colors duration-300 hover:border-lav">
              {t.common.viewWork}<ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
            </Link>
          </motion.div>
        </div>
        <div className="label mt-14 flex items-center justify-between border-t border-white/10 pt-5 text-mist">
          <span className="flex items-center gap-2"><ArrowDown className="h-3.5 w-3.5 animate-bounce" />{t.common.scroll}</span>
          <a href={EMAIL_HREF} className="flex items-center gap-2 hover:text-ivory" data-testid="hero-email-link"><Mail className="h-3.5 w-3.5 text-lav" />{EMAIL}</a>
        </div>
      </div>
      <Star className="absolute right-[8%] top-[22%] hidden h-14 w-14 text-ivory/80 md:block" />
    </section>
  );
};

const Services = () => {
  const { t } = useLang();
  return (
    <section className="mx-auto max-w-[1440px] px-5 py-28 md:px-10 md:py-40" data-testid="home-services">
      <div className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <SectionLabel index="01">{t.services.label}</SectionLabel>
          <Reveal><h2 className="mt-6 font-display text-4xl font-bold leading-none tracking-tight text-ivory md:text-5xl">{t.services.title}</h2></Reveal>
        </div>
        <ul className="lg:col-span-8">
          {t.services.items.map(([title, body], i) => (
            <Reveal key={title} as="li" delay={i * 0.06}>
              <div className="group grid grid-cols-12 items-baseline gap-4 border-t border-white/10 py-8 transition-colors duration-500 hover:border-lav" data-testid={`service-item-${i}`}>
                <span className="label col-span-2 text-mist group-hover:text-lav">0{i + 1}</span>
                <h3 className="col-span-10 font-display text-3xl font-semibold uppercase tracking-tight text-ivory transition-transform duration-500 group-hover:translate-x-3 md:col-span-5 md:text-4xl">{title}</h3>
                <p className="col-span-10 col-start-3 text-sm leading-relaxed text-mist md:col-span-5">{body}</p>
              </div>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
};

const FeaturedWork = () => {
  const { t, path } = useLang();
  return (
    <section className="mx-auto max-w-[1440px] px-5 pb-28 md:px-10 md:pb-40" data-testid="home-work">
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <SectionLabel index="02">{t.work.label}</SectionLabel>
          <Reveal><h2 className="mt-6 max-w-xl font-display text-4xl font-bold leading-none tracking-tight text-ivory md:text-6xl">{t.work.title}</h2></Reveal>
        </div>
        <Link to={path("work")} className="label link-star text-ivory" data-testid="home-all-work-link">{t.common.viewWork} →</Link>
      </div>
      <div className="mt-16 grid gap-10 md:grid-cols-2">
        {t.work.projects.map((p, i) => <WorkCard key={p.slug} p={p} i={i} offset={i === 1} />)}
      </div>
    </section>
  );
};

const Why = () => {
  const { t } = useLang();
  return (
    <section className="bg-ivory py-28 text-ink md:py-40" data-testid="home-why">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <SectionLabel index="03" tone="text-ink/60">{t.why.label}</SectionLabel>
        <Reveal><h2 className="mt-6 font-display text-5xl font-extrabold uppercase leading-[0.9] tracking-tight md:text-7xl">{t.why.title}</h2></Reveal>
        <div className="mt-20 grid gap-px bg-ink/10 md:grid-cols-2">
          {t.why.items.map(([title, body], i) => (
            <Reveal key={title} delay={i * 0.07} className="bg-ivory">
              <div className="group h-full p-8 md:p-12" data-testid={`why-item-${i}`}>
                <Star className="h-8 w-8 text-lav-deep transition-transform duration-700 group-hover:rotate-90" />
                <h3 className="mt-10 font-display text-2xl font-bold leading-tight tracking-tight md:text-3xl">{title}</h3>
                <p className="mt-4 max-w-sm text-ink/65">{body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const PricingPreview = () => {
  const { t, path } = useLang();
  return (
    <section className="mx-auto max-w-[1440px] px-5 py-28 md:px-10 md:py-40" data-testid="home-pricing-preview">
      <SectionLabel index="04">{t.pricing.label}</SectionLabel>
      <Reveal><h2 className="mt-6 font-display text-4xl font-bold leading-none tracking-tight text-ivory md:text-6xl">{t.pricing.preview}</h2></Reveal>
      <div className="mt-16">
        {t.pricing.plans.map((p, i) => (
          <Reveal key={p.key} delay={i * 0.06}>
            <Link to={`${path("contact")}?package=${p.key}`} data-testid={`home-plan-row-${p.key}`}
              className={`group grid grid-cols-12 items-center gap-4 border-t py-8 transition-colors duration-500 ${p.key === "professional" ? "border-lav/60" : "border-white/10"} hover:bg-white/[0.02]`}>
              <span className="label col-span-2 text-mist md:col-span-1">0{i + 1}</span>
              <span className="col-span-10 flex items-center gap-4 font-display text-3xl font-bold uppercase tracking-tight text-ivory md:col-span-5 md:text-5xl">
                {p.name}
                {p.key === "professional" && <span className="label rounded-full border border-lav px-3 py-1 text-[10px] text-lav">{t.pricing.recommended}</span>}
              </span>
              <span className="col-span-10 col-start-3 text-sm text-mist md:col-span-4">{p.desc}</span>
              <span className="col-span-12 text-right font-display text-4xl font-bold tracking-tighter text-ivory transition-colors group-hover:text-lav md:col-span-2 md:text-5xl">{p.price}</span>
            </Link>
          </Reveal>
        ))}
        <div className="flex flex-col justify-between gap-6 border-t border-white/10 pt-8 md:flex-row md:items-center">
          <div className="flex flex-wrap items-center gap-4">
            <span className="font-display text-2xl text-ivory">{t.maint.name} — {t.maint.price}{t.maint.per}</span>
            <span className="rounded-sm bg-acid px-3 py-1 font-display text-sm font-extrabold uppercase text-ink">{t.maint.free}</span>
          </div>
          <Link to={path("services")} className="label group flex items-center gap-2 text-lav" data-testid="home-see-pricing-link">
            {t.common.seePricing}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee items={useLang().t.marquee} />
      <Services />
      <FeaturedWork />
      <Why />
      <PricingPreview />
      <CtaBand />
    </>
  );
}
